import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_CITY, agoLabel, clearRecents as clearStored, getLocation, getRecents, onLocationChange, setCity as store,
} from '../services/userLocation';
import { geocodeCity, reverseGeocode } from '../utils/reverseGeocode';

/**
 * The city the app is showing events near, as React state (top-header handoff, Oct 14 2026).
 *
 * One hook for every surface: the header pill, the location panel, the FILTERS tab, the Cities
 * filter's NEAR line, search's COMING UP NEAR heading, Featured and the wall's fetch. Changing
 * it in one place changes it in all of them, in this tab and in any other one open.
 *
 * It never moves on its own — see `services/userLocation`. `useCurrentLocation()` is the single
 * door to the device's own position, and only a tap opens it.
 */
export default function useUserLocation({ fallbackCity = '' } = {}) {
  const [loc, setLoc] = useState(getLocation);
  const [recents, setRecents] = useState(getRecents);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => onLocationChange((next) => { setLoc(next); setRecents(getRecents()); }), []);

  const setCity = useCallback((city, coords = null) => {
    setError(null);
    store(city, coords);
    // Nothing saved the coordinates for a city picked by name: ask once, in the background.
    if (!coords) geocodeCity(city).catch(() => null);
  }, []);

  const clearRecents = useCallback(() => clearStored(), []);

  /** The ONLY path that reads the device's position, and only when it is tapped. */
  const useCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) { setError('This browser can’t locate you — pick a town'); return; }
    setLocating(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        const name = await reverseGeocode(lat, lng);
        setLocating(false);
        if (!name) { setError('Couldn’t name that place — pick a town'); return; }
        store(name, { lat, lng });
      },
      () => { setLocating(false); setError('Location blocked — pick a town'); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  }, []);

  const city = loc.city || fallbackCity || DEFAULT_CITY;
  return {
    city,
    // `saved` is false until the person has actually chosen: the pill still shows a city, but
    // nothing has been pinned, so a profile city can still fill in.
    saved: !!loc.city,
    lat: loc.lat,
    lng: loc.lng,
    recents,
    recentsLabelled: recents.filter((r) => r.name.toLowerCase() !== city.toLowerCase()).map((r) => ({ ...r, when: agoLabel(r.at) })),
    setCity,
    clearRecents,
    useCurrentLocation,
    locating,
    error,
  };
}
